import { useState, useEffect } from 'react'
import {
  Mail, Check, RefreshCw, Plus, Trash2, MoreHorizontal,
  Pencil, X, ChevronRight,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { mailService } from '@/services/studioService'

// ── Forslag til hva man kan sende ─────────────────────────────────────────────
const SUGGESTIONS = [
  { icon: '📊', text: 'Lag ukentlig salgsrapport fra vedlagt Excel-fil' },
  { icon: '📧', text: 'Svar på alle uleste kundehenvendelser i innboksen min' },
  { icon: '📝', text: 'Oppsummer møtenotater fra vedlagt PDF og send til teamet' },
  { icon: '🔍', text: 'Analyser nettstedstrafikk og lag en rapport med grafer' },
  { icon: '📱', text: 'Post ukentlig innholdsoppdatering på Instagram og LinkedIn' },
  { icon: '🗂️', text: 'Organiser og kategoriser filene i Google Drive-mappen min' },
]

// ── Godkjente avsendere (mock-data, kan kobles til API) ───────────────────────
function useApprovedSenders(userEmail: string | undefined) {
  const [senders, setSenders] = useState<string[]>([])
  useEffect(() => {
    if (userEmail) setSenders([userEmail])
  }, [userEmail])
  function add(email: string) {
    if (!senders.includes(email)) setSenders(prev => [...prev, email])
  }
  function remove(email: string) {
    setSenders(prev => prev.filter(s => s !== email))
  }
  return { senders, add, remove }
}

// ── Settings-fane ─────────────────────────────────────────────────────────────
function SettingsTab({
  mailAddress,
  loadingAddress,
  userEmail,
}: {
  mailAddress: string
  loadingAddress: boolean
  userEmail: string | undefined
}) {
  const [copied, setCopied] = useState(false)
  const [editingAddress, setEditingAddress] = useState(false)
  const [showAddSender, setShowAddSender] = useState(false)
  const [newSender, setNewSender] = useState('')
  const [senderMenuOpen, setSenderMenuOpen] = useState<string | null>(null)
  const { senders, add, remove } = useApprovedSenders(userEmail)

  function handleCopy() {
    navigator.clipboard.writeText(mailAddress).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleAddSender(e: React.FormEvent) {
    e.preventDefault()
    if (newSender.trim() && newSender.includes('@')) {
      add(newSender.trim())
      setNewSender('')
      setShowAddSender(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Sine's email row */}
      <div className="sm-settings-row">
        <div className="sm-settings-row-left">
          <div className="sm-settings-row-title">Sines e-post</div>
          <div className="sm-settings-row-desc">Send e-poster til denne adressen for å opprette oppgaver.</div>
        </div>
        <div className="sm-settings-row-right">
          {loadingAddress ? (
            <span style={{ fontSize: 13, color: '#5A5A5A' }}>Laster...</span>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="sm-email-address">{mailAddress}</span>
              <button
                className="sm-icon-btn"
                onClick={handleCopy}
                title={copied ? 'Kopiert!' : 'Kopier adresse'}
              >
                {copied ? <Check size={13} color="#5EB92D" /> : <Pencil size={13} />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Workflow email row */}
      <div className="sm-settings-row">
        <div className="sm-settings-row-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className="sm-settings-row-title">Arbeidsflyt-e-post</div>
            <div className="sm-help-badge">?</div>
          </div>
          <div className="sm-settings-row-desc">Tilpass e-postadresser og instruksjoner for å behandle ulike oppgaver.</div>
        </div>
        <div className="sm-settings-row-right">
          <button className="sm-add-btn" onClick={() => setEditingAddress(true)}>
            <Plus size={13} />
            Legg til arbeidsflyt-e-post
          </button>
          {editingAddress && (
            <div className="sm-add-sender-form" style={{ marginTop: 8 }}>
              <input
                type="email"
                placeholder="e-post@eksempel.no"
                className="sm-input"
                autoFocus
                onKeyDown={e => e.key === 'Escape' && setEditingAddress(false)}
              />
              <button className="sm-icon-btn" onClick={() => setEditingAddress(false)}>
                <X size={13} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Approved senders */}
      <div className="sm-settings-row sm-settings-row--block">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div className="sm-settings-row-title">Godkjente avsendere</div>
            <div className="sm-settings-row-desc">Kun e-poster fra disse adressene kan opprette oppgaver.</div>
          </div>
          <button className="sm-add-btn" onClick={() => setShowAddSender(v => !v)}>
            <Plus size={13} />
            Legg til godkjent avsender
          </button>
        </div>

        {showAddSender && (
          <form onSubmit={handleAddSender} className="sm-add-sender-form" style={{ marginBottom: 10 }}>
            <input
              type="email"
              placeholder="e-post@eksempel.no"
              value={newSender}
              onChange={e => setNewSender(e.target.value)}
              className="sm-input"
              autoFocus
            />
            <button type="submit" className="sm-add-btn" style={{ flexShrink: 0 }}>Legg til</button>
            <button type="button" className="sm-icon-btn" onClick={() => setShowAddSender(false)}>
              <X size={13} />
            </button>
          </form>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {senders.map(sender => (
            <div key={sender} className="sm-sender-row">
              <Mail size={13} color="#5A5A5A" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, color: '#C5C5C5' }}>{sender}</span>
              <div style={{ position: 'relative' }}>
                <button
                  className="sm-icon-btn"
                  onClick={() => setSenderMenuOpen(senderMenuOpen === sender ? null : sender)}
                >
                  <MoreHorizontal size={14} />
                </button>
                {senderMenuOpen === sender && (
                  <div className="sm-dropdown">
                    <button
                      className="sm-dropdown-item sm-dropdown-item--danger"
                      onClick={() => { remove(sender); setSenderMenuOpen(null) }}
                    >
                      <Trash2 size={12} /> Fjern
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Suggestions section */}
      <div style={{ padding: '20px 0 0' }}>
        <div className="sm-section-label">Forslag til hva du kan sende</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              className="sm-suggestion-row"
              onClick={() => {
                navigator.clipboard.writeText(s.text)
              }}
            >
              <span style={{ fontSize: 15, flexShrink: 0 }}>{s.icon}</span>
              <span style={{ flex: 1, fontSize: 13, color: '#A5A5A5', textAlign: 'left', lineHeight: 1.4 }}>{s.text}</span>
              <ChevronRight size={13} color="#3A3A3A" style={{ flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Inbox-fane ────────────────────────────────────────────────────────────────
function InboxTab() {
  const [tasks, setTasks] = useState<{
    id: string; sender: string; subject: string; status: string; created_at: string
  }[]>([])
  const [loading, setLoading] = useState(true)

  const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    received: { label: 'Mottatt', color: '#888' },
    started:  { label: 'Startet', color: '#5EB92D' },
    queued:   { label: 'I kø', color: '#F5A623' },
    test:     { label: 'Test', color: '#6B7280' },
    error:    { label: 'Feil', color: '#EF4444' },
  }

  function refresh() {
    setLoading(true)
    mailService.listTasks()
      .then(setTasks)
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { refresh() }, [])

  return (
    <div>
      {/* Table header */}
      <div className="sm-inbox-header">
        <span className="sm-inbox-col sm-inbox-col--sender">Avsender</span>
        <span className="sm-inbox-col sm-inbox-col--content">Innhold</span>
        <span className="sm-inbox-col sm-inbox-col--date">Dato</span>
        <button className="sm-icon-btn" onClick={refresh} title="Oppdater">
          <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Rows */}
      {tasks.length === 0 ? (
        <div style={{ padding: '60px 0', textAlign: 'center', color: '#3A3A3A', fontSize: 13 }}>
          Ingen data
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {tasks.map(task => {
            const s = STATUS_LABELS[task.status] || { label: task.status, color: '#888' }
            return (
              <div key={task.id} className="sm-inbox-row">
                <span className="sm-inbox-col sm-inbox-col--sender" style={{ color: '#A5A5A5', fontSize: 13 }}>
                  {task.sender}
                </span>
                <span className="sm-inbox-col sm-inbox-col--content" style={{ color: '#C5C5C5', fontSize: 13 }}>
                  {task.subject || '(uten emne)'}
                </span>
                <span className="sm-inbox-col sm-inbox-col--date" style={{ color: '#5A5A5A', fontSize: 12 }}>
                  {new Date(task.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })}
                </span>
                <span style={{ fontSize: 11, color: s.color, flexShrink: 0, minWidth: 50, textAlign: 'right' }}>
                  {s.label}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Hoved-komponent ───────────────────────────────────────────────────────────
export function SineMailContent() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'settings' | 'inbox'>('settings')
  const [mailAddress, setMailAddress] = useState('')
  const [loadingAddress, setLoadingAddress] = useState(true)

  useEffect(() => {
    if (!user) return
    mailService.getAddress()
      .then(data => setMailAddress(data.address))
      .catch(() => {
        const shortId = (user.id || 'me').replace(/-/g, '').slice(0, 12)
        setMailAddress(`agent-${shortId}@mail.sine.no`)
      })
      .finally(() => setLoadingAddress(false))
  }, [user])

  return (
    <div className="sm-root">
      {/* Title */}
      <h2 className="settings-title" style={{ marginBottom: 16 }}>Mail Sine</h2>

      {/* Info banner */}
      <div className="sm-info-banner">
        <Mail size={14} color="#5A5A5A" style={{ flexShrink: 0 }} />
        <span>Opprett oppgaver via e-post. CC andre for å samarbeide.</span>
        <a href="https://sine.no/docs/mail" target="_blank" rel="noopener noreferrer" className="sm-info-link">
          Lær mer ↗
        </a>
      </div>

      {/* Tabs */}
      <div className="sm-tabs">
        <button
          className={`sm-tab${activeTab === 'settings' ? ' active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Innstillinger
        </button>
        <button
          className={`sm-tab${activeTab === 'inbox' ? ' active' : ''}`}
          onClick={() => setActiveTab('inbox')}
        >
          Innboks
        </button>
      </div>

      {/* Content */}
      <div style={{ marginTop: 4 }}>
        {activeTab === 'settings' ? (
          <SettingsTab
            mailAddress={mailAddress}
            loadingAddress={loadingAddress}
            userEmail={user?.email}
          />
        ) : (
          <InboxTab />
        )}
      </div>
    </div>
  )
}
